import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["code", "ruleRow", "list", "ruleCode", "deleteBtn", "cloneBtn"]

  connect() {
    document.addEventListener("turbo:frame-load", (event) => {
      if (event.target.id !== "bonus-hour-rule-form") return

      if (this.pendingCode && this.hasRuleCodeTarget) {
        this.ruleCodeTarget.value = this.pendingCode
        this.pendingCode = null
      }
    })
  }

  generateCode() {
    const ids = Array.from(this.ruleRowTargets || [])
      .map(row => {
        const el = row.querySelector(".id")
        return el ? el.textContent.trim().toUpperCase() : null
      })
      .filter(Boolean)

    const re = /^PRE(\d+)$/i
    let max = 0
    ids.forEach(id => {
      const m = id.match(re)
      if (m) {
        const n = parseInt(m[1], 10)
        if (!Number.isNaN(n)) max = Math.max(max, n)
      }
    })

    const next = max + 1
    const code = "PRE" + String(next).padStart(3, "0")

    return code
  }

  addNewRow() {
    const code = this.generateCode()

    const form = this.element.querySelector("#bonus-hour-rule-form-element")
    if (form) form.reset()

    if (this.hasRuleCodeTarget) {
      this.ruleCodeTarget.value = code
    }

    let newRow

    if (this.ruleRowTargets.length) {
      newRow = this.ruleRowTargets[0].cloneNode(true)
      newRow.removeAttribute("href")
      newRow.removeAttribute("data-turbo-frame")
    } else {
      newRow = document.createElement("div")
      newRow.className = "rule-row"
      newRow.innerHTML = `
        <div class="id"></div>
        <div class="meta">
          <div class="name">(名称未設定)</div>
          <div class="sub">
            OT: 0.0 / 深夜: 0.0 / 休日: 0.0
          </div>
        </div>
        <div class="badge">--:--</div>
      `
    }

    newRow.id = `bonus_hour_rule_${code}`

    newRow.setAttribute(
      "data-bonus-hour-rules--form-target",
      "ruleRow"
    )

    newRow.setAttribute(
      "data-action",
      "click->bonus-hour-rules--form#activateRow"
    )

    this.pendingCode = code

    newRow.querySelector(".id").textContent = code
    newRow.querySelector(".name").textContent = "(名称未設定)"
    newRow.querySelector(".sub").textContent = "OT: 0.0 / 深夜: 0.0 / 休日: 0.0"
    newRow.querySelector(".badge").textContent = "--:--"

    this.ruleRowTargets.forEach(row => row.classList.remove("selected"))
    newRow.classList.add("selected")

    this.listTarget.prepend(newRow)

    if (this.hasRuleCodeTarget) {
      this.ruleCodeTarget.value = code
    }

    if (this.hasDeleteBtnTarget) {
      const btn = this.deleteBtnTarget

      btn.removeAttribute("href")
      btn.removeAttribute("data-turbo-method")
      btn.removeAttribute("data-turbo-confirm")

      btn.dataset.action = "bonus-hour-rules--form#removeTemplate"
    }
  }

  removeTemplate(event) {
    event.preventDefault()

    const activeRow = this.ruleRowTargets.find(row =>
      row.classList.contains("selected")
    )

    if (!activeRow) return

    const nextRow = activeRow.nextElementSibling
    const prevRow = activeRow.previousElementSibling

    activeRow.remove()

    const newActive = nextRow || prevRow

    if (newActive) {
      newActive.classList.add("selected")
      newActive.dispatchEvent(new Event("click", { bubbles: true }))
    } else {
      const form = this.element.querySelector("#bonus-hour-rule-form-element")
      form?.reset()
    }
  }

  activateRow(event) {
    const row = event.currentTarget

    this.ruleRowTargets.forEach(r => r.classList.remove("selected"))
    row.classList.add("selected")

    if (!row.getAttribute("href")) {
      const frame = document.querySelector("#bonus-hour-rule-form")

      if (frame) {
        frame.src = "/bonus_hour_rules/new"
      }

      const form = this.element.querySelector("#bonus-hour-rule-form-element")

      if (form) {
        const createPath = form.dataset.createPath
        if (createPath) form.setAttribute("action", createPath)

        const methodInput = form.querySelector('input[name="_method"]')
        if (methodInput) methodInput.remove()
      }

      if (this.hasDeleteBtnTarget) {
        const btn = this.deleteBtnTarget

        btn.removeAttribute("href")
        btn.removeAttribute("data-turbo-method")
        btn.removeAttribute("data-turbo-confirm")

        btn.dataset.action = "bonus-hour-rules--form#removeTemplate"
      }

      if (this.hasCloneBtnTarget) {
        const btn = this.cloneBtnTarget
        btn.dataset.action = "bonus-hour-rules--form#cloneRow"
        btn.removeAttribute("href")
      }
    } else {
      const deleteUrl = row.getAttribute("href").replace("/edit", "")

      if (this.hasDeleteBtnTarget) {
        const btn = this.deleteBtnTarget
        const code = row.querySelector(".id")?.textContent.trim()

        btn.href = deleteUrl
        btn.dataset.turboMethod = "delete"
        btn.dataset.turboConfirm = `削除しますか？ (${code})`

        btn.removeAttribute("data-action")
      }

      if (this.hasCloneBtnTarget) {
        const cloneUrl = row.getAttribute("href").replace("/edit", "/clone")

        const btn = this.cloneBtnTarget
        btn.href = cloneUrl
        btn.dataset.turboMethod = "post"
        btn.removeAttribute("data-action")
      }
    }
  }

  loadTemplateToForm(row) {
    const form = this.element.querySelector("#bonus-hour-rule-form-element")
    if (!form) return

    const code = row.querySelector(".id")?.textContent.trim()

    const inputs = form.querySelectorAll(
      "input:not([type=submit]):not([type=button]):not([type=hidden]), select, textarea"
    )

    inputs.forEach(input => {
      if (input.name === "bonus_hour_rule[code]") return

      if (input.type === "checkbox" || input.type === "radio") {
        input.checked = false
      } else {
        input.value = ""
      }
    })

    // set code
    const codeField = form.querySelector('[name="bonus_hour_rule[code]"]')
    if (codeField) codeField.value = code

    // set default values
    const overtime = form.querySelector('[name="bonus_hour_rule[over_time_coefficient]"]')
    const night = form.querySelector('[name="bonus_hour_rule[night_time_coefficient]"]')
    const holiday = form.querySelector('[name="bonus_hour_rule[holiday_coefficient]"]')

    const daily = form.querySelector('[name="bonus_hour_rule[daily_normal_work_time]"]')
    const weekly = form.querySelector('[name="bonus_hour_rule[weekly_normal_work_time]"]')

    if (overtime) overtime.value = "0.0"
    if (night) night.value = "0.0"
    if (holiday) holiday.value = "0.0"

    if (daily) daily.value = "0"
    if (weekly) weekly.value = "0"

    const formAction = form.getAttribute("action")

    if (formAction && formAction.match(/bonus_hour_rules\/\d+/)) {
      const createPath = form.dataset.createPath
      if (createPath) form.setAttribute("action", createPath)

      const methodInput = form.querySelector('input[name="_method"]')
      if (methodInput) methodInput.remove()
    }
  }
}
